'use client';

import * as React from 'react';
import { useTranslations } from 'next-intl';
import * as Sentry from '@sentry/nextjs';

import { styled } from '@unity/cloud-ui/styles';
import { Box, Button, MenuItem, Select, Stack, TextareaAutosize, Typography } from '@unity/cloud-ui';

import { createFeedbackTicket } from '@/lib/jira';

type FeedbackState = 'closed' | 'open' | 'submitted' | 'error';

const StyledTextareaAutosize = styled(TextareaAutosize)(({ theme }) => ({
  borderRadius: 6,
  padding: 12,
  borderColor: theme.vars.palette.action.outlinedBorder,
  maxWidth: '100%',
  minWidth: '100%',
  minHeight: '2rem',
  maxHeight: '70vh',
  backgroundColor: theme.vars.palette.default.main,
  '::placeholder': {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    color: theme.vars.palette.text.secondary,
    opacity: 1,
  },
}));

export const FeedbackForm = ({ pageTitle }: { pageTitle: string }) => {
  const [feedbackText, setFeedbackText] = React.useState('');
  const [feedbackType, setFeedbackType] = React.useState('-1');
  const [feedbackState, setFeedbackState] = React.useState<FeedbackState>('closed');
  const [loading, setLoading] = React.useState(false);
  const t = useTranslations();

  const problemTypes = [
    { id: '-1', text: t('feedback:problem-type:none') },
    { id: '47800', text: t('feedback:problem-type:need-code-samples') },
    { id: '47803', text: t('feedback:problem-type:code-samples-not-working') },
    { id: '47801', text: t('feedback:problem-type:missing-information') },
    { id: '47802', text: t('feedback:problem-type:incorrect-information') },
    { id: '47805', text: t('feedback:problem-type:unclear-confusing-information') },
    { id: '47804', text: t('feedback:problem-type:spelling-error') },
    { id: '47806', text: t('feedback:problem-type:other') },
  ];

  const handleShowFeedbackInput = () => {
    setFeedbackState('open');
  };

  const handleCancelFeedback = () => {
    setFeedbackState('closed');
  };

  const handleSubmitFeedback = async () => {
    setLoading(true);
    try {
      await createFeedbackTicket({
        feedbackText,
        pageTitle,
        feedbackType,
      });
      setFeedbackState('submitted');
    } catch (error) {
      Sentry.captureException('Failed to submit feedback', error);
      setFeedbackState('error');
    }
    setLoading(false);
  };

  const renderClosedState = () => {
    return (
      <>
        <Typography variant="h5" mb={2} component="div">
          {t('feedback:report-a-problem')}
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          value={t('feedback:button:title')}
          aria-label={t('feedback:button:aria-label')}
          onClick={handleShowFeedbackInput}
        >
          {t('feedback:button:title')}
        </Button>
      </>
    );
  };

  const renderOpenState = () => {
    return (
      <>
        <Typography variant="h6" mb={2} component="div">
          {t('feedback:report-a-problem')}
        </Typography>

        <Stack spacing={1}>
          <Select
            value={feedbackType}
            size="small"
            aria-label={t('feedback:select:aria-label')}
            onChange={(e) => {
              setFeedbackType(e.target.value);
            }}
          >
            {problemTypes.map((value) => {
              return (
                <MenuItem key={value.id} value={value.id}>
                  {value.text}
                </MenuItem>
              );
            })}
          </Select>
          <StyledTextareaAutosize
            minRows={4}
            placeholder={t('feedback:placeholder')}
            aria-label={t('feedback:text:aria-label')}
            value={feedbackText}
            onChange={(e) => {
              setFeedbackText(e.target.value);
            }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              color="secondary"
              value={t('button:cancel')}
              aria-label={t('feedback:cancel:aria-label')}
              onClick={handleCancelFeedback}
            >
              {t('button:cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={!feedbackText}
              value={t('button:submit')}
              aria-label={t('feedback:submit:aria-label')}
              loading={loading}
              onClick={handleSubmitFeedback}
            >
              {t('button:submit')}
            </Button>
          </Box>
        </Stack>
      </>
    );
  };

  const getFeedbackSection = () => {
    switch (feedbackState) {
      case 'closed':
        return renderClosedState();
      case 'open':
        return renderOpenState();
      case 'submitted':
        return <Typography variant="body2">{t('feedback:thank-you')}</Typography>;
      case 'error':
        return (
          <Typography variant="body2" color="error">
            {t('feedback:error')}
          </Typography>
        );
    }
  };

  return (
    <Box id="feedback-form" padding={1}>
      {getFeedbackSection()}
    </Box>
  );
};
